import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Checkbox } from './ui/checkbox';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from './ui/collapsible';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import {
  Calculator,
  ChevronDown,
  ChevronRight,
  Info,
  AlertTriangle,
  Download,
  RotateCcw,
  DollarSign,
  Clock,
  CheckCircle2,
  XCircle,
  FileText,
  Layers
} from 'lucide-react';

// Commission eligibility rules
const COMMISSION_RULES = {
  EHR: { waitingMonths: 1, label: 'EHR', rate: 0.15 },
  PMS: { waitingMonths: 1, label: 'Practice Management', rate: 0.15 },
  CCM: { waitingMonths: 3, label: 'Chronic Care Management', rate: 0.10 },
  APCM: { waitingMonths: 3, label: 'Advanced Primary Care', rate: 0.10 },
  RPM: { waitingMonths: 3, label: 'Remote Patient Monitoring', rate: 0.10 },
  RCM: { waitingMonths: 2, label: 'Revenue Cycle Management', rate: 0.06 },
};

const QUALIFIED_LEAD_RATE = 200;

// Format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

// Product Commission Card Component
const ProductCard = ({ product, config, data, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  const getStatus = () => {
    if (!data.invoiceSent) return { status: 'pending', message: 'Awaiting Invoice', color: 'text-amber-600' };
    if (!data.paymentReceived) return { status: 'pending', message: 'Awaiting Payment', color: 'text-amber-600' };
    if (data.monthsSinceInvoice < config.waitingMonths) {
      return { 
        status: 'pending', 
        message: `Waiting Period (${config.waitingMonths - data.monthsSinceInvoice} months remaining)`, 
        color: 'text-amber-600' 
      };
    }
    return { status: 'eligible', message: 'Eligible for Commission Credit', color: 'text-emerald-600' };
  };
  
  const status = getStatus();
  const isEligible = status.status === 'eligible';
  
  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <div 
          className="flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 rounded-lg cursor-pointer transition-colors border border-gray-200"
          data-testid={`product-card-${product}`}
        >
          <div className="flex items-center gap-3">
            {isOpen ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-500" />}
            <span className="font-medium text-gray-900">{config.label}</span>
            {data.dealsClosed > 0 && (
              <span className="text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded-full">
                {data.dealsClosed} deal{data.dealsClosed > 1 ? 's' : ''}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {isEligible ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
            ) : (
              <Clock className="w-4 h-4 text-amber-500" />
            )}
            <span className={`text-sm ${status.color}`}>{isEligible ? 'Eligible' : 'Pending'}</span>
          </div>
        </div>
      </CollapsibleTrigger>
      
      <CollapsibleContent>
        <div className="p-4 border border-t-0 border-gray-200 rounded-b-lg bg-white space-y-4">
          {/* Deals Closed */}
          <div className="space-y-2">
            <Label className="text-xs text-gray-600">Number of Deals Closed</Label>
            <Input
              type="number"
              min="0"
              value={data.dealsClosed}
              onChange={(e) => onChange(product, 'dealsClosed', parseInt(e.target.value) || 0)}
              className="h-9"
              data-testid={`${product}-deals-input`}
            />
          </div>
          
          {/* Toggles */}
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <Label className="text-xs text-gray-600">Invoice Sent?</Label>
              <Switch
                checked={data.invoiceSent}
                onCheckedChange={(checked) => onChange(product, 'invoiceSent', checked)}
                data-testid={`${product}-invoice-toggle`}
              />
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <Label className="text-xs text-gray-600">Payment Received?</Label>
              <Switch
                checked={data.paymentReceived}
                onCheckedChange={(checked) => onChange(product, 'paymentReceived', checked)}
                data-testid={`${product}-payment-toggle`}
              />
            </div>
          </div>
          
          {/* Months Since Invoice */}
          <div className="space-y-2">
            <Label className="text-xs text-gray-600">Months Since First Invoice</Label>
            <Input
              type="number"
              min="0"
              value={data.monthsSinceInvoice}
              onChange={(e) => onChange(product, 'monthsSinceInvoice', parseInt(e.target.value) || 0)}
              className="h-9"
              data-testid={`${product}-months-input`}
            />
          </div>
          
          {/* Status */}
          <div className={`p-3 rounded-lg ${isEligible ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`}>
            <div className="flex items-center gap-2">
              {isEligible ? (
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              ) : (
                <Clock className="w-4 h-4 text-amber-600" />
              )}
              <span className={`text-sm font-medium ${status.color}`}>{status.message}</span>
            </div>
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

// Main Commission Calculator Component
const CommissionCalculator = ({ className = '' }) => {
  // View mode: 'current' or 'projected'
  const [viewMode, setViewMode] = useState('current');
  
  // Section 1: Qualified Lead Earnings
  const [completedDemos, setCompletedDemos] = useState(0);
  
  // Section 2: Product Commission Data
  const [productData, setProductData] = useState({
    EHR: { dealsClosed: 0, invoiceSent: false, paymentReceived: false, monthsSinceInvoice: 0 },
    PMS: { dealsClosed: 0, invoiceSent: false, paymentReceived: false, monthsSinceInvoice: 0 },
    CCM: { dealsClosed: 0, invoiceSent: false, paymentReceived: false, monthsSinceInvoice: 0 },
    APCM: { dealsClosed: 0, invoiceSent: false, paymentReceived: false, monthsSinceInvoice: 0 },
    RPM: { dealsClosed: 0, invoiceSent: false, paymentReceived: false, monthsSinceInvoice: 0 },
    RCM: { dealsClosed: 0, invoiceSent: false, paymentReceived: false, monthsSinceInvoice: 0 },
  });
  
  // Section 3: Bundle Builder
  const [bundleSelections, setBundleSelections] = useState({
    EHR: { selected: false, monthlyRevenue: 0, commissionRate: 15 },
    PMS: { selected: false, monthlyRevenue: 0, commissionRate: 15 },
    CCM: { selected: false, monthlyRevenue: 0, commissionRate: 10 },
    APCM: { selected: false, monthlyRevenue: 0, commissionRate: 10 },
    RPM: { selected: false, monthlyRevenue: 0, commissionRate: 10 },
    RCM: { selected: false, monthlyRevenue: 0, commissionRate: 6 },
  });
  
  // Disclaimer acknowledgment
  const [disclaimerAcknowledged, setDisclaimerAcknowledged] = useState(false);
  const [disclaimerOpen, setDisclaimerOpen] = useState(false);
  
  // Handle product data change
  const handleProductChange = useCallback((product, field, value) => {
    setProductData(prev => ({
      ...prev,
      [product]: { ...prev[product], [field]: value }
    }));
  }, []);
  
  // Handle bundle selection change
  const handleBundleChange = useCallback((product, field, value) => {
    setBundleSelections(prev => ({
      ...prev,
      [product]: { ...prev[product], [field]: value }
    }));
  }, []);
  
  // Calculate qualified lead earnings
  const qualifiedLeadEarnings = completedDemos * QUALIFIED_LEAD_RATE;
  
  // Calculate product commissions
  const calculateProductCommission = (product) => {
    const data = productData[product];
    const config = COMMISSION_RULES[product];
    const isEligible = data.invoiceSent && data.paymentReceived && data.monthsSinceInvoice >= config.waitingMonths;
    
    if (!isEligible) return { eligible: 0, pending: data.dealsClosed > 0 ? data.dealsClosed * 1000 : 0 };
    return { eligible: data.dealsClosed * 1000, pending: 0 }; // Simplified calculation
  };
  
  // Calculate bundle commission
  const calculateBundleCommission = () => {
    let total = 0;
    Object.entries(bundleSelections).forEach(([product, data]) => {
      if (data.selected && data.monthlyRevenue > 0) {
        const commission = data.monthlyRevenue * (data.commissionRate / 100);
        total += commission;
      }
    });
    return total;
  };
  
  // Calculate totals
  const eligibleCommission = Object.keys(productData).reduce((sum, product) => {
    return sum + calculateProductCommission(product).eligible;
  }, 0);
  
  const pendingCommission = Object.keys(productData).reduce((sum, product) => {
    return sum + calculateProductCommission(product).pending;
  }, 0);
  
  const bundleCommission = calculateBundleCommission();
  const grandTotal = qualifiedLeadEarnings + eligibleCommission + bundleCommission;
  
  // Reset calculator
  const handleReset = () => {
    setCompletedDemos(0);
    setProductData({
      EHR: { dealsClosed: 0, invoiceSent: false, paymentReceived: false, monthsSinceInvoice: 0 },
      PMS: { dealsClosed: 0, invoiceSent: false, paymentReceived: false, monthsSinceInvoice: 0 },
      CCM: { dealsClosed: 0, invoiceSent: false, paymentReceived: false, monthsSinceInvoice: 0 },
      APCM: { dealsClosed: 0, invoiceSent: false, paymentReceived: false, monthsSinceInvoice: 0 },
      RPM: { dealsClosed: 0, invoiceSent: false, paymentReceived: false, monthsSinceInvoice: 0 },
      RCM: { dealsClosed: 0, invoiceSent: false, paymentReceived: false, monthsSinceInvoice: 0 },
    });
    setBundleSelections({
      EHR: { selected: false, monthlyRevenue: 0, commissionRate: 15 },
      PMS: { selected: false, monthlyRevenue: 0, commissionRate: 15 },
      CCM: { selected: false, monthlyRevenue: 0, commissionRate: 10 },
      APCM: { selected: false, monthlyRevenue: 0, commissionRate: 10 },
      RPM: { selected: false, monthlyRevenue: 0, commissionRate: 10 },
      RCM: { selected: false, monthlyRevenue: 0, commissionRate: 6 },
    });
    setDisclaimerAcknowledged(false);
  };
  
  // Export summary
  const handleExport = () => {
    if (!disclaimerAcknowledged) {
      setDisclaimerOpen(true);
      return;
    }
    
    const summary = {
      date: new Date().toISOString(),
      qualifiedLeadEarnings,
      eligibleCommission,
      pendingCommission,
      bundleCommission,
      grandTotal,
      products: productData,
      bundle: bundleSelections,
    };
    
    const blob = new Blob([JSON.stringify(summary, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `commission-summary-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
  };

  return (
    <TooltipProvider>
      <Card className={`border border-gray-200 shadow-sm bg-white ${className}`} data-testid="commission-calculator">
        {/* Header */}
        <CardHeader className="pb-2 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Calculator className="w-5 h-5" />
                Commission Calculator
              </CardTitle>
              <p className="text-xs text-gray-500 mt-1">
                Estimate qualified lead earnings and commission credit eligibility.
              </p>
            </div>
            <div className="flex items-center gap-2">
              {/* View Mode Toggle */}
              <div className="flex bg-gray-100 rounded-lg p-0.5">
                <button
                  onClick={() => setViewMode('current')}
                  className={`px-3 py-1 text-xs rounded-md transition-colors ${
                    viewMode === 'current' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
                  }`}
                >
                  Current
                </button>
                <button
                  onClick={() => setViewMode('projected')}
                  className={`px-3 py-1 text-xs rounded-md transition-colors ${
                    viewMode === 'projected' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
                  }`}
                >
                  Projected
                </button>
              </div>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="p-4 space-y-6 max-h-[600px] overflow-y-auto">
          {/* Section 1: Qualified Lead Earnings */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-gray-700" />
              <h3 className="font-medium text-gray-900">Qualified Lead Earnings</h3>
              <Tooltip>
                <TooltipTrigger>
                  <Info className="w-3.5 h-3.5 text-gray-400" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs text-xs">
                  A qualified lead = demo scheduled with a physician or decision maker AND demo completed.
                </TooltipContent>
              </Tooltip>
            </div>
            
            <div className="p-4 bg-gray-50 rounded-lg space-y-3">
              <div className="space-y-2">
                <Label className="text-xs text-gray-600">Number of Completed Qualified Demos</Label>
                <Input
                  type="number"
                  min="0"
                  value={completedDemos}
                  onChange={(e) => setCompletedDemos(parseInt(e.target.value) || 0)}
                  className="h-9 bg-white"
                  data-testid="qualified-demos-input"
                />
                <p className="text-xs text-gray-500">
                  {formatCurrency(QUALIFIED_LEAD_RATE)} × {completedDemos} demos
                </p>
              </div>
              
              <motion.div 
                className="p-3 bg-white rounded-lg border border-gray-200"
                animate={{ scale: [1, 1.02, 1] }}
                transition={{ duration: 0.2 }}
                key={qualifiedLeadEarnings}
              >
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Qualified Lead Earnings:</span>
                  <span className="text-lg font-semibold text-gray-900">{formatCurrency(qualifiedLeadEarnings)}</span>
                </div>
                <p className="text-xs text-emerald-600 mt-1">This amount is always immediately eligible.</p>
              </motion.div>
            </div>
          </div>
          
          {/* Section 2: Solution Commission Builder */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Layers className="w-4 h-4 text-gray-700" />
              <h3 className="font-medium text-gray-900">Solution Commission Builder</h3>
            </div>
            
            <div className="space-y-2">
              {Object.entries(COMMISSION_RULES).map(([product, config]) => (
                <ProductCard
                  key={product}
                  product={product}
                  config={config}
                  data={productData[product]}
                  onChange={handleProductChange}
                />
              ))}
            </div>
          </div>
          
          {/* Section 3: Bundle Builder */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-gray-700" />
              <h3 className="font-medium text-gray-900">Cumulative Bundle Builder</h3>
              <Tooltip>
                <TooltipTrigger>
                  <Info className="w-3.5 h-3.5 text-gray-400" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs text-xs">
                  Select multiple solutions to build a bundle and calculate projected commission.
                </TooltipContent>
              </Tooltip>
            </div>
            
            <div className="p-4 bg-gray-50 rounded-lg space-y-3">
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(bundleSelections).map(([product, data]) => (
                  <div 
                    key={product}
                    className={`p-3 rounded-lg border transition-colors ${
                      data.selected ? 'bg-white border-gray-900' : 'bg-gray-100 border-gray-200'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Checkbox
                        checked={data.selected}
                        onCheckedChange={(checked) => handleBundleChange(product, 'selected', checked)}
                        data-testid={`bundle-${product}-checkbox`}
                      />
                      <span className="text-sm font-medium text-gray-900">{product}</span>
                    </div>
                    
                    {data.selected && (
                      <div className="space-y-2 mt-2">
                        <div>
                          <Label className="text-xs text-gray-500">Monthly Revenue</Label>
                          <Input
                            type="number"
                            min="0"
                            value={data.monthlyRevenue}
                            onChange={(e) => handleBundleChange(product, 'monthlyRevenue', parseFloat(e.target.value) || 0)}
                            className="h-8 text-sm"
                            placeholder="$0.00"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500">Commission %</Label>
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            value={data.commissionRate}
                            onChange={(e) => handleBundleChange(product, 'commissionRate', parseFloat(e.target.value) || 0)}
                            className="h-8 text-sm"
                          />
                        </div>
                        <p className="text-xs text-gray-600">
                          Commission: {formatCurrency(data.monthlyRevenue * (data.commissionRate / 100))}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              
              {bundleCommission > 0 && (
                <motion.div 
                  className="p-3 bg-white rounded-lg border border-gray-200"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Bundle Commission Total:</span>
                    <span className="text-lg font-semibold text-gray-900">{formatCurrency(bundleCommission)}</span>
                  </div>
                </motion.div>
              )}
            </div>
          </div>
          
          {/* Disclaimer Section */}
          <Collapsible open={disclaimerOpen} onOpenChange={setDisclaimerOpen}>
            <CollapsibleTrigger asChild>
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
                <span className="text-sm text-gray-700">Commission Disclaimer & Payment Terms</span>
                {disclaimerOpen ? <ChevronDown className="w-4 h-4 ml-auto" /> : <ChevronRight className="w-4 h-4 ml-auto" />}
              </div>
            </CollapsibleTrigger>
            
            <CollapsibleContent>
              <div className="p-4 bg-gray-50 rounded-b-lg border-t border-gray-200 space-y-4 text-xs text-gray-600">
                <div>
                  <h4 className="font-medium text-gray-700 mb-2">Commission Calculation Disclaimer</h4>
                  <p>This calculator is provided for informational and estimation purposes only. All earnings, commission credits, bundle projections, and payout timelines displayed are projected estimates based on user-entered information and standard commission timing rules.</p>
                  <p className="mt-2">Actual commission payments are subject to the terms and conditions outlined in the Independent Contractor Agreement and Sales Agent Referral Fee Policy.</p>
                </div>
                
                <div>
                  <h4 className="font-medium text-gray-700 mb-2">Payment Timing Disclaimer</h4>
                  <ul className="list-disc pl-4 space-y-1">
                    <li>EHR / PMS – Eligible after first invoice and confirmed payment (1 month)</li>
                    <li>CCM / APCM / RPM – Eligible after three (3) months of invoicing and confirmed payment</li>
                    <li>RCM – Eligible after two (2) months of invoicing and confirmed payment</li>
                  </ul>
                </div>
                
                <div>
                  <h4 className="font-medium text-gray-700 mb-2">No Guarantee of Earnings</h4>
                  <p>This tool does not guarantee earnings, payout timing, deal closure, client retention, or revenue realization. All calculations are projections only and should not be relied upon for financial planning, tax planning, or contractual entitlement.</p>
                </div>
                
                <div className="flex items-start gap-2 p-3 bg-white rounded-lg border border-gray-200">
                  <Checkbox
                    id="disclaimer-ack"
                    checked={disclaimerAcknowledged}
                    onCheckedChange={setDisclaimerAcknowledged}
                    data-testid="disclaimer-checkbox"
                  />
                  <label htmlFor="disclaimer-ack" className="text-xs text-gray-700 cursor-pointer">
                    I acknowledge that these calculations are estimates only.
                  </label>
                </div>
                
                <p className="text-xs text-gray-400">Last Updated: February 2026</p>
              </div>
            </CollapsibleContent>
          </Collapsible>
        </CardContent>
        
        {/* Sticky Summary Footer */}
        <div className="border-t border-gray-200 bg-gray-50 p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="p-3 bg-white rounded-lg border border-gray-200">
              <p className="text-xs text-gray-500">Qualified Lead Earnings</p>
              <p className="text-lg font-semibold text-emerald-600">{formatCurrency(qualifiedLeadEarnings)}</p>
              <p className="text-xs text-emerald-500">Immediate</p>
            </div>
            <div className="p-3 bg-white rounded-lg border border-gray-200">
              <p className="text-xs text-gray-500">Pending Commission</p>
              <p className="text-lg font-semibold text-amber-600">{formatCurrency(pendingCommission)}</p>
              <p className="text-xs text-amber-500">Awaiting eligibility</p>
            </div>
            <div className="p-3 bg-white rounded-lg border border-gray-200">
              <p className="text-xs text-gray-500">Eligible Commission</p>
              <p className="text-lg font-semibold text-gray-900">{formatCurrency(eligibleCommission)}</p>
              <p className="text-xs text-gray-500">Ready for payout</p>
            </div>
            <div className="p-3 bg-white rounded-lg border border-gray-200">
              <p className="text-xs text-gray-500">Bundle Projection</p>
              <p className="text-lg font-semibold text-gray-900">{formatCurrency(bundleCommission)}</p>
              <p className="text-xs text-gray-500">Projected</p>
            </div>
          </div>
          
          {/* Grand Total */}
          <motion.div 
            className="p-4 bg-gray-900 text-white rounded-lg"
            animate={{ scale: [1, 1.01, 1] }}
            transition={{ duration: 0.3 }}
            key={grandTotal}
          >
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-300">Grand Total Earnings Projection</span>
              <span className="text-2xl font-bold">{formatCurrency(grandTotal)}</span>
            </div>
          </motion.div>
          
          {/* Action Buttons */}
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleReset}
              className="flex-1"
              data-testid="reset-calculator-btn"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Reset
            </Button>
            <Button
              size="sm"
              onClick={handleExport}
              className={`flex-1 ${disclaimerAcknowledged ? 'bg-gray-900 hover:bg-gray-800' : 'bg-gray-400 cursor-not-allowed'}`}
              disabled={!disclaimerAcknowledged}
              data-testid="export-summary-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export Summary
            </Button>
          </div>
        </div>
      </Card>
    </TooltipProvider>
  );
};

export default CommissionCalculator;
